/// <reference types="@types/google.maps" />

import React, { useEffect, useRef, useState } from "react";
import { Input } from "antd";
// ✅ Import standalone functions instead of the Loader class
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { DecriptEnv } from "../helpers/utils";

export interface GoogleAddressResult {
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  lat: number;
  lng: number;
  area_id?: string;
}

export interface LatLngBoundsLiteral {
  south: number;
  west: number;
  north: number;
  east: number;
}

export const EUROPE_BOUNDS: LatLngBoundsLiteral = {
  south: 34.5, // Southern Greece / Crete & Southern Spain
  west: -25.0, // Iceland / Azores
  north: 71.5, // Northern Scandinavia
  east: 40.0,  // Eastern Europe
};

interface Props {
  onPlaceSelect: (place: GoogleAddressResult) => void;
  defaultValue?: string;
  placeholder?: string;
  countryCode?: string[];
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  bounds?: LatLngBoundsLiteral;
  strictBounds?: boolean;
}

let isGoogleMapsConfigured = false;

const DEFAULT_COUNTRY_CODE = ["gb"];

const GoogleAddressAutocomplete: React.FC<Props> = ({
  onPlaceSelect,
  defaultValue,
  placeholder = "Enter your address",
  countryCode = DEFAULT_COUNTRY_CODE,
  disabled = false,
  value,
  onChange,
  bounds,
  strictBounds = false,
}) => {
  const inputRef = useRef<any>(null);
  const [inputValue, setInputValue] = useState(value || defaultValue || "");

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    } else {
      setInputValue("");
    }
  }, [value]);
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  const mapKey = DecriptEnv(process.env.REACT_APP_MAP_KEY);

  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        // 1. Configure global options (API Key, version, etc.)
        if (!isGoogleMapsConfigured) {
          setOptions({
            key: mapKey,
            v: "weekly",
            libraries: ["places"],
          });
          isGoogleMapsConfigured = true;
        }

        // 2. Import the library directly
        const { Autocomplete } = (await importLibrary(
          "places",
        )) as google.maps.PlacesLibrary;

        // 3. Ensure input is mounted
        const nativeInput = inputRef.current?.input;
        if (!nativeInput) return;

        // 4. Initialize Autocomplete or Update Restrictions
        const options: google.maps.places.AutocompleteOptions = {
          fields: ["address_components", "geometry", "formatted_address"],
        };

        if (countryCode && countryCode.length > 0) {
          options.componentRestrictions = { country: countryCode };
        }

        if (bounds) {
          options.bounds = new google.maps.LatLngBounds(
            { lat: bounds.south, lng: bounds.west },
            { lat: bounds.north, lng: bounds.east }
          );
          options.strictBounds = strictBounds;
        }

        if (!autoCompleteRef.current) {
          autoCompleteRef.current = new Autocomplete(nativeInput, options);

          autoCompleteRef.current.addListener("place_changed", () => {
            const place = autoCompleteRef.current?.getPlace();
            if (place) {
              handlePlaceSelect(place);
            }
          });

          console.info("Google Maps Places library loaded");
        } else {
          if (countryCode && countryCode.length > 0) {
            autoCompleteRef.current.setComponentRestrictions({ country: countryCode });
          } else {
            autoCompleteRef.current.setComponentRestrictions(null as any);
          }
          if (bounds) {
            autoCompleteRef.current.setBounds(
              new google.maps.LatLngBounds(
                { lat: bounds.south, lng: bounds.west },
                { lat: bounds.north, lng: bounds.east }
              )
            );
            (autoCompleteRef.current as any).set("strictBounds", strictBounds);
          }
        }
      } catch (error) {
        console.error("Google Maps failed to load", error);
      }
    };

    if (mapKey) {
      loadGoogleMaps();
    }
  }, [mapKey, countryCode, bounds, strictBounds]);

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    const addressComponents = place.address_components || [];
    const geometry = place.geometry;

    const getComponent = (type: string) =>
      addressComponents.find((c) => c.types.includes(type))?.long_name || "";

    const streetNumber = getComponent("street_number");
    const route = getComponent("route");
    const mainAddress = streetNumber
      ? `${streetNumber} ${route}`
      : route || place.formatted_address || "";

    const result: GoogleAddressResult = {
      address: mainAddress,
      city:
        getComponent("locality") ||
        getComponent("postal_town") ||
        getComponent("sublocality"),
      state: getComponent("administrative_area_level_1"),
      zip: getComponent("postal_code"),
      country: getComponent("country"),
      lat: geometry?.location?.lat() || 0,
      lng: geometry?.location?.lng() || 0,
    };

    setInputValue(result.address);
    if (onChange) onChange(result.address);
    (onPlaceSelectRef.current || onPlaceSelect)(result);
    console.log("place ", result);
  };

  return (
    <Input
      ref={inputRef}
      placeholder={placeholder}
      className="form-control form-control-lg"
      value={inputValue}
      onChange={(e) => {
        setInputValue(e.target.value);
        if (onChange) onChange(e.target.value);
      }}
      disabled={disabled}
    />
  );
};

export default GoogleAddressAutocomplete;
